import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { logger } from "@/lib/logger";

const MAX_POLL_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 2000;

/**
 * Poll a payment status endpoint until it reports paid, times out, or errors.
 * Handles both providers via a single URL builder; caller decides the URL.
 *
 * Returns: { status: "polling" | "paid" | "timeout" | "error", order }
 */
export default function usePaymentPolling(url, { enabled = true } = {}) {
  const [status, setStatus] = useState("polling");
  const [order, setOrder] = useState(null);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const triesRef = useRef(0);

  useEffect(() => {
    if (!enabled || !url) return undefined;
    cancelledRef.current = false;
    triesRef.current = 0;

    const tick = async () => {
      try {
        const { data } = await client.get(url);
        if (cancelledRef.current) return;
        if (data.payment_status === "paid" || data.status === "paid") {
          setOrder(data);
          setStatus("paid");
          return;
        }
        triesRef.current += 1;
        if (triesRef.current > MAX_POLL_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelledRef.current) return;
        logger.error("Payment polling failed:", err);
        setStatus("error");
      }
    };
    tick();

    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [url, enabled]);

  return { status, order };
}
